import { ArrowTrendingDownIcon, ArrowTrendingUpIcon, MinusIcon } from '@heroicons/react/24/solid';
import { AutoTextSize } from 'auto-text-size';
import { useCallback, useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router';
import snippets from '../i18n/lang';
import { classNames, getSign } from '../utils';
import formatNumber from '../utils/formatNumber';
import { SubHeadline } from './Headline';

export const ClubList = ({ clubs }) => {
  const TrendingIcon = useCallback((change) => {
    if (change === 0) return <MinusIcon className="text-primary-500 w-6 h-6 sm:w-8 sm:h-8" />;
    if (change > 0)
      return <ArrowTrendingUpIcon className="text-primary-500 max-[330px]:hidden w-6 h-6 sm:w-8 sm:h-8" />;
    if (change < 0)
      return <ArrowTrendingDownIcon className="text-primary-500  max-[330px]:hidden w-6 h-6 sm:w-8 sm:h-8" />;
  }, []);

  const sortedClubs = useMemo(
    () => (clubs ? [...clubs].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'de')) : null),
    [clubs],
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between items-center">
        <SubHeadline className="mb-1">{snippets.customerDetail.clubOverviewHeadline}</SubHeadline>
      </div>
      {sortedClubs
        ? sortedClubs.map((item, i) => {
            return (
              <Link
                key={item.name + i + item.clubID}
                className="p-3 rounded-xl bg-gray-100 border-gray-200 flex flex-col items-center gap-2 hover:bg-gray-50 cursor-pointer"
                to={window.location.pathname + `/${item.backendID}$${item.clubID}${window.location.search}`}
              >
                <div className="mr-auto text-sm font-normal text-gray-500">{item.name}</div>
                <div className="flex justify-between items-end overflow-hidden gap-2 w-full">
                  <div className="max-[330px]:hidden self-center">
                    {TrendingIcon(item.activemembers - item.activemembersReference)}
                  </div>
                  <ClubStat
                    large
                    value={item.activemembers}
                    signValue={false}
                    name="Aktive Mitglieder"
                    compareValue={
                      item.activemembers > 0 &&
                      ((item.activemembers / (item.activemembersReference || 1) - 1) * 100)?.toFixed(1)
                    }
                  />

                  <ClubStat signValue={false} value={item.activations} name="Zugänge" />
                  <ClubStat signValue={false} value={item.cancelledMembers} name="Abgänge" />
                  <ClubStat signValue={false} value={item.terminations} name="Kündigungen" light />
                </div>
              </Link>
            );
          })
        : Array(5)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-xl bg-gray-100 border-gray-200 flex flex-col items-center gap-3 hover:bg-gray-50 cursor-pointer"
              >
                <div className="mr-auto text-lg font-normal text-gray-500">
                  <Skeleton className="w-[100px]" />
                </div>

                <div className="flex gap-4 justify-between  w-full">
                  <ArrowTrendingDownIcon className="text-gray-600 w-9 h-9" />
                  <ClubStat loading signValue={false} name="Aktive Mitglieder" />
                  <ClubStat loading name="Zugänge" />
                  <ClubStat loading name="Abgänge" />
                  <ClubStat loading name="Kündigungen" light />
                </div>
              </div>
            ))}
    </div>
  );
};

export const ClubStat = ({
  value = null,
  loading = false,
  name,
  light = false,
  large = false,
  Icon = null,
  compareValue = null,
  signValue = true,
  textColor = 'text-gray-600',
}) => {
  return (
    <div className="flex flex-col w-full items-start grow-0">
      <strong
        className={classNames(
          large ? 'text-2xl sm:text-2xl' : 'text-xl sm:text-xl',
          'font-medium flex overflow-hidden gap-1 items-center justify-center',
          textColor,
          light && 'opacity-55',
        )}
      >
        {loading ? (
          <Skeleton width={40} />
        ) : (
          <>
            {Icon && <Icon className={'hidden sm:block w-6 h-6 ' + textColor} />}
            {signValue && getSign(value)}
            {formatNumber(value)}
            {!!compareValue && (
              <small className={classNames('block text-xs ', compareValue > 0 ? 'text-primary-500' : 'text-gray-400')}>
                {compareValue === 'NaN' ? (
                  '-'
                ) : (
                  <div
                    className={classNames(
                      'break-keep ',
                      compareValue < 0 && 'text-gray-500',
                      compareValue === 0 && 'text-gray-400',
                      getSign(compareValue) === '+' && 'text-primary-400',
                    )}
                  >
                    {`${getSign(compareValue)}${formatNumber(compareValue)}%`}
                  </div>
                )}
              </small>
            )}
          </>
        )}
      </strong>

      <div className="flex grow-0 shrink-0 w-full text-gray-400 text-ellipsis overflow-hidden leading-3">
        <AutoTextSize maxFontSizePx={12} minFontSizePx={8} mode="boxoneline">
          {name}
        </AutoTextSize>
      </div>
    </div>
  );
};
